import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Pagination,
  Snackbar,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import { getTransactions, deleteTransaction } from '../../services/productapi';
import { useTransactionContext } from '../../contexts/TransactionContext';

import { format } from 'date-fns';

const TransactionTable = () => {
  const { refreshTrigger } = useTransactionContext();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
      // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20); // Use constant value, no setter
  const [totalCount, setTotalCount] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Transaction type and marketplace options
  const transactionTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'sale', label: 'Sale' },
    { value: 'return', label: 'Return' }
  ];

  const marketplaceOptions = [
    { value: 'all', label: 'All Marketplaces' },
    { value: 'internal', label: 'Internal' },
    { value: 'amazon', label: 'Amazon' },
    { value: 'flipkart', label: 'Flipkart' },
    { value: 'myntra', label: 'Myntra' },
    { value: 'meesho', label: 'Meesho' },
    { value: 'other', label: 'Other' }
  ];

  // Helper function to fetch data with filters
  const fetchDataWithFilters = async (pageNumber = null, type = null, marketplace = null, dateRange = null, isInitialLoad = false) => {
    if (loading && !isInitialLoad) {
      console.log('Fetch already in progress, skipping duplicate request');
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(false);
      setError(null);
      
      // Use provided parameters or current state
      const currentPage = pageNumber || page;
      const currentType = type !== null ? type : typeFilter;
      const currentMarketplace = marketplace !== null ? marketplace : marketplaceFilter;
      const currentDateRange = dateRange || { from: fromDate, to: toDate };
      
      console.log(`Fetching transactions for page ${currentPage}, type: ${currentType}, marketplace: ${currentMarketplace}, date range: ${currentDateRange.from} - ${currentDateRange.to}`);
      
      // Prepare params
      const params = {
        page: currentPage,
        page_size: rowsPerPage,
      };
      
      // Add type filter if not 'all'
      if (currentType !== 'all') {
        params.type = currentType; // Backend expects 'type' not 'transaction_type'
      }
      
      // Add marketplace filter if not 'all'
      if (currentMarketplace !== 'all') {
        params.marketplace = currentMarketplace;
      }
        // Add date range filters if provided
      if (currentDateRange.from) {
        params.start_date = currentDateRange.from;
      }
      
      if (currentDateRange.to) {
        params.end_date = currentDateRange.to;
      }
      
      // Add cache buster
      params._t = new Date().getTime();
      
      console.log('Sending API request with params:', params);
      const response = await getTransactions(params);
        // Handle paginated response
      if (response.data.results) {
        console.log(`Received ${response.data.results.length} transactions, total: ${response.data.count}`);
        setTransactions(response.data.results);
        setTotalCount(response.data.count);
        // Don't modify rowsPerPage based on results length
      } else {
        // Fallback for non-paginated response
        console.log(`Received ${response.data.length} transactions`);
        setTransactions(response.data);
        setTotalCount(response.data.length);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions. Please try again.');
      
      setNotification({
        open: true,
        message: 'Error: Failed to load transactions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  // Load transactions on initial mount
  useEffect(() => {
    fetchDataWithFilters(1, typeFilter, marketplaceFilter, { from: fromDate, to: toDate }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Listen for refresh triggers from other components
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Reset to page 1 and refresh data when a new transaction is added
      setPage(1);
      fetchDataWithFilters(1, typeFilter, marketplaceFilter, { from: fromDate, to: toDate }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);
    // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Use search function if in search mode, otherwise use regular fetch
    if (isSearchMode && searchTerm) {
      handleSearch(searchTerm, value, typeFilter, marketplaceFilter, { from: fromDate, to: toDate });
    } else {
      fetchDataWithFilters(value, typeFilter, marketplaceFilter, { from: fromDate, to: toDate });
    }
  };
  
  // Handle transaction type filter change
  const handleTypeFilterChange = (e) => {
    const newType = e.target.value;
    setTypeFilter(newType);
    if (isSearchMode && searchTerm) {
      handleSearch(searchTerm, 1, newType, marketplaceFilter);
    } else {
      fetchDataWithFilters(1, newType, marketplaceFilter);
    }
    setPage(1);
  };
  
  // Handle marketplace filter change
  const handleMarketplaceFilterChange = (e) => {
    const newMarketplace = e.target.value;
    setMarketplaceFilter(newMarketplace);
    if (isSearchMode && searchTerm) {
      handleSearch(searchTerm, 1, typeFilter, newMarketplace);
    } else {
      fetchDataWithFilters(1, typeFilter, newMarketplace);
    }
    setPage(1);
  };
  
  // Handle from date change
  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);
    
    // Apply filter
    if (isSearchMode && searchTerm) {
      handleSearch(searchTerm, 1, typeFilter, marketplaceFilter, { from: newFromDate, to: toDate });
    } else {
      fetchDataWithFilters(1, typeFilter, marketplaceFilter, { from: newFromDate, to: toDate });
    }
    setPage(1);
  };
  
  // Handle to date change
  const handleToDateChange = (e) => {
    const newToDate = e.target.value;
    setToDate(newToDate);
    
    // Apply filter
    if (isSearchMode && searchTerm) {
      handleSearch(searchTerm, 1, typeFilter, marketplaceFilter, { from: fromDate, to: newToDate });
    } else {
      fetchDataWithFilters(1, typeFilter, marketplaceFilter, { from: fromDate, to: newToDate });
    }
    setPage(1);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle search execution
  const handleSearch = async (query = searchTerm, pageNumber = 1, type = typeFilter, marketplace = marketplaceFilter, dateRange = { from: fromDate, to: toDate }) => {
    if (!query.trim()) {
      // If search is cleared, revert to normal data fetching
      fetchDataWithFilters(1, type, marketplace, dateRange);
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(true);
      setError(null);
      
      const params = {
        q: query,
        page: pageNumber,
        page_size: rowsPerPage,
      };
      
      // Add type filter if not 'all'
      if (type !== 'all') {
        params.type = type; // Backend expects 'type' not 'transaction_type'
      }
      
      // Add marketplace filter if not 'all'
      if (marketplace !== 'all') {
        params.marketplace = marketplace;
      }
        // Add date range filters if provided
      if (dateRange.from) {
        params.start_date = dateRange.from;
      }
      
      if (dateRange.to) {
        params.end_date = dateRange.to;
      }
      
      // Add cache buster
      params._t = new Date().getTime();
        const response = await getTransactions(params);
      const data = response.data;
        if (data.results) {
        setTransactions(data.results);
        setTotalCount(data.count);
        setPage(pageNumber);
      } else {
        setTransactions(data);
        setTotalCount(data.length);
        setPage(1);
      }
    } catch (err) {
      console.error('Error searching transactions:', err);
      setError('Search failed. Please try again.');
      
      setNotification({
        open: true,
        message: 'Error: Search failed',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search submission (e.g., on Enter key press)
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
    // Handle refresh button click
  const handleRefresh = () => {
    setSearchTerm('');
    setFromDate('');
    setToDate('');
    setIsSearchMode(false);
    fetchDataWithFilters(1, typeFilter, marketplaceFilter, { from: '', to: '' });
    setPage(1);
  };
  
  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type) => {
    switch(type) {
      case 'purchase':
        return <Chip label="Purchase" color="primary" size="small" />;
      case 'sale':
        return <Chip label="Sale" color="success" size="small" />;
      case 'return':
        return <Chip label="Return" color="error" size="small" />;
      default:
        return <Chip label={type} size="small" />;
    }
  };

  // Format timestamp
  const formatDate = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return timestamp;
    }
  };

  // Handle transaction deletion
  const handleDeleteTransaction = async (transactionId) => {
    try {
      await deleteTransaction(transactionId);
      setNotification({
        open: true,
        message: 'Transaction deleted successfully',
        severity: 'success'
      });
      // Refresh transaction data
      fetchDataWithFilters(page, typeFilter, marketplaceFilter, { from: fromDate, to: toDate });
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setNotification({
        open: true,
        message: 'Error deleting transaction. Please try again.',
        severity: 'error'
      });
    }
  };

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Open delete confirmation dialog
  const openDeleteDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDialogOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }} elevation={0}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {/* Search field */}
          <TextField
            placeholder="Search SKU, product name ..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      if (isSearchMode) handleRefresh();
                    }}
                  >
                    &times;
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          {/* Transaction type filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="type-filter-label">Transaction Type</InputLabel>
            <Select
              labelId="type-filter-label"
              id="type-filter"
              value={typeFilter}
              onChange={handleTypeFilterChange}
              label="Transaction Type"
              startAdornment={<FilterListIcon sx={{ mr: 1, ml: -0.5 }} fontSize="small" />}
            >
              {transactionTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Marketplace filter */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="marketplace-filter-label">Marketplace</InputLabel>
            <Select
              labelId="marketplace-filter-label"
              id="marketplace-filter"
              value={marketplaceFilter}
              onChange={handleMarketplaceFilterChange}
              label="Marketplace"
              startAdornment={<FilterListIcon sx={{ mr: 1, ml: -0.5 }} fontSize="small" />}
            >
              {marketplaceOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            {/* Date range filters */}
          <TextField
            id="from-date"
            label="From Date"
            type="date"
            variant="outlined"
            size="small"
            value={fromDate}
            onChange={handleFromDateChange}
            sx={{ minWidth: 170 }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.54)' }} />
            }}
          />
          
          <TextField
            id="to-date"
            label="To Date"
            type="date"
            variant="outlined"
            size="small"
            value={toDate}
            onChange={handleToDateChange}
            sx={{ minWidth: 170 }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.54)' }} />
            }}
          />
          
          {/* Refresh button */}
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} color="primary" sx={{ border: '1px solid #e0e0e0' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Loading status */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Error message */}
        {error && !loading && (
          <Box sx={{ my: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        
        {/* No results message */}
        {!loading && !error && transactions.length === 0 && (
          <Box sx={{ my: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <Typography>No transactions found.</Typography>
          </Box>
        )}
        
        {/* Transactions table */}
        {!loading && !error && transactions.length > 0 && (
          <TableContainer component={Paper} elevation={0} sx={{ mb: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="transactions table">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Transaction</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Marketplace</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Actions</TableCell>  
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>{transaction.variant_sku}</TableCell>
                    <TableCell>
                      {transaction.product_name}
                      {(transaction.color_name || transaction.variant_category) && (
                        <Typography variant="body2" color="text.secondary">
                          {[
                            transaction.variant_category && `Category: ${transaction.variant_category}`,
                            transaction.color_name && `Color: ${transaction.color_name}`
                          ].filter(Boolean).join(' | ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getTransactionTypeDisplay(transaction.transaction_type)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: transaction.quantity < 0 ? 'error.main' : 'success.main',
                          fontWeight: 'medium'
                        }}
                      >
                        {transaction.quantity < 0 ? transaction.quantity : `+${transaction.quantity}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.marketplace_display}</TableCell>
                    <TableCell>{formatDate(transaction.timestamp)}</TableCell>
                    
                    {/* Actions column - Delete button */}
                    <TableCell align="center" sx={{ width: 100 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(transaction)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Pagination */}
        {!loading && !error && transactions.length > 0 && totalCount > rowsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination
              count={Math.ceil(totalCount / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog with detailed transaction information */}
      <Dialog
        open={dialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-confirmation-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Transaction Deletion
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to delete this transaction? This action cannot be undone.
              </DialogContentText>
              
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Transaction Details:
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.5, pl: 1 }}>
                  <Typography variant="body2" fontWeight="bold">SKU:</Typography>
                  <Typography variant="body2">{selectedTransaction.variant_sku}</Typography>
                  
                  <Typography variant="body2" fontWeight="bold">Product:</Typography>
                  <Typography variant="body2">{selectedTransaction.product_name}</Typography>
                  
                  {selectedTransaction.variant_category && (
                    <>
                      <Typography variant="body2" fontWeight="bold">Category:</Typography>
                      <Typography variant="body2">{selectedTransaction.variant_category}</Typography>
                    </>
                  )}
                  
                  {selectedTransaction.color_name && (
                    <>
                      <Typography variant="body2" fontWeight="bold">Color:</Typography>
                      <Typography variant="body2">{selectedTransaction.color_name}</Typography>
                    </>
                  )}
                  
                  <Typography variant="body2" fontWeight="bold">Transaction:</Typography>
                  <Typography variant="body2">{selectedTransaction.transaction_type_display || selectedTransaction.transaction_type}</Typography>
                  
                  <Typography variant="body2" fontWeight="bold">Quantity:</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: selectedTransaction.quantity < 0 ? 'error.main' : 'success.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {selectedTransaction.quantity < 0 ? selectedTransaction.quantity : `+${selectedTransaction.quantity}`}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (selectedTransaction) {
                handleDeleteTransaction(selectedTransaction.id);
                closeDeleteDialog();
              }
            }} 
            color="error"
            variant="contained"
          >
            Delete Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransactionTable;
